import { useJobillaOrgId, useQuotaRequestMore } from '@/hooks'
import { cn } from '@/lib'
import { Button, SubtleLoader } from '@/ui'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import {
	type PlanData,
	type PlanDataTier,
	planDataTier,
	type PlanInterval,
	planInterval,
	usePlanIndex,
} from '@jobilla/nexus-sdk'
import { upperFirst } from 'lodash'
import { useTranslation } from 'react-i18next'
import { formatPrice } from './format'

const PRICING_URL = 'https://www.jobilla.com/pricing'

interface PlanStepProps {
	profileId: string | null
	interval: PlanInterval
	onIntervalChange: (interval: PlanInterval) => void
	onChoose: (tier: PlanDataTier) => void
}

export function PlanStep({ profileId, interval, onIntervalChange, onChoose }: PlanStepProps) {
	const { t } = useTranslation('common')
	const organisationId = useJobillaOrgId()
	const { data: plans, isLoading, isError, refetch } = usePlanIndex(
		organisationId,
		profileId ? { profile: profileId } : undefined,
		{ enabled: !!organisationId },
	)

	const selfServePlans = (plans ?? []).filter((plan) => plan.selfServe && plan[interval] !== null)
	const offersEnterprise = (plans ?? []).some((plan) => !plan.selfServe)

	return (
		<div className="space-y-6">
			<div className="flex flex-col items-center gap-4 text-center">
				<h1 className="text-2xl font-bold text-grey-900">{t('subscribe.plans.title')}</h1>
				<IntervalToggle interval={interval} onChange={onIntervalChange} />
			</div>

			{isLoading ? <SubtleLoader delay={0} /> : null}

			{isError
				? (
					<div className="mx-auto max-w-lg rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
						<p className="mb-3">{t('subscribe.plans.loadError')}</p>
						<Button type="neutral" onClick={() => refetch()}>{t('common.tryAgain')}</Button>
					</div>
				)
				: null}

			{plans
				? (
					<div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
						{selfServePlans.map((plan) => (
							<PlanCard key={plan.tier} plan={plan} interval={interval} onChoose={onChoose} />
						))}
						{offersEnterprise ? <EnterpriseCard /> : null}
					</div>
				)
				: null}

			<p className="text-center text-sm text-grey-500">
				<a
					href={PRICING_URL}
					target="_blank"
					rel="noreferrer"
					className="inline-flex items-center gap-1 font-medium text-brand-700 hover:underline"
				>
					{t('subscribe.plans.compare')}
					<ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
				</a>
			</p>
		</div>
	)
}

function IntervalToggle(
	{ interval, onChange }: { interval: PlanInterval; onChange: (interval: PlanInterval) => void },
) {
	const { t } = useTranslation('common')

	return (
		<div className="inline-flex items-center rounded-full border border-grey-200 bg-white p-1" role="radiogroup">
			{[planInterval.monthly, planInterval.yearly].map((value) => (
				<button
					key={value}
					type="button"
					role="radio"
					aria-checked={interval === value}
					onClick={() => onChange(value)}
					className={cn(
						'rounded-full px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer',
						interval === value ? 'bg-grey-900 text-white' : 'text-grey-600 hover:text-grey-900',
					)}
				>
					{t(`subscribe.interval.${value}`)}
				</button>
			))}
		</div>
	)
}

function PlanCard(
	{ plan, interval, onChoose }: { plan: PlanData; interval: PlanInterval; onChoose: (tier: PlanDataTier) => void },
) {
	const { t, i18n } = useTranslation('common')
	const price = plan[interval]

	if (!price) {
		return null
	}

	return (
		<div className="flex flex-col rounded-xl border border-grey-200 bg-white p-5">
			<h2 className="mb-4 text-lg font-bold text-grey-900">{upperFirst(plan.tier)}</h2>
			<div className="mb-6 flex-1">
				<span className="text-2xl font-bold text-grey-900">{formatPrice(price, i18n.language)}</span>
				<span className="text-sm text-grey-500">{t(`subscribe.per.${interval}`)}</span>
				<p className="mt-1 text-xs text-grey-400">{t('subscribe.plans.vatExcluded')}</p>
			</div>
			<Button type="committal" fullWidth={true} onClick={() => onChoose(plan.tier)}>
				{t('subscribe.plans.choose', { plan: upperFirst(plan.tier) })}
			</Button>
		</div>
	)
}

function EnterpriseCard() {
	const { t } = useTranslation('common')
	const { request, hasSent, isPending, isError, isDisabled } = useQuotaRequestMore(null, 'upgrade-request:enterprise')

	return (
		<div className="flex flex-col rounded-xl border border-grey-900 bg-grey-900 p-5 text-white">
			<h2 className="mb-4 text-lg font-bold">{upperFirst(planDataTier.enterprise)}</h2>
			<p className="mb-6 flex-1 text-sm text-grey-300">{t('subscribe.plans.enterpriseDesc')}</p>
			<Button type="subtle" fullWidth={true} onClick={request} disabled={isDisabled} isLoading={isPending}>
				{hasSent ? t('subscribe.plans.enterpriseSent') : t('subscribe.plans.contactSales')}
			</Button>
			{hasSent ? <p className="mt-2 text-xs text-grey-300">{t('subscribe.plans.enterpriseSentNote')}</p> : null}
			{isError ? <p className="mt-2 text-xs text-red-300">{t('subscribe.plans.enterpriseFailed')}</p> : null}
		</div>
	)
}
